import window from "global";
import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { UPDATE_SETTINGS } from "@ugg/shared/api/graphql/queries";
import { ALL_ROLE_OPTIONS } from "@ugg/shared/query-params/filter-options/common";
import { TIER_LIST_OPTIONS } from "@ugg/shared/query-params/filter-options/tier-lists/tier-list";
import { validParams } from "@ugg/shared/query-params/valid-params";
import ButtonSuccess from "components/Buttons/ButtonSuccess";
import { Checkbox } from "@ugg/shared/components/common/Checkbox";
import QueueType from "@ugg/shared/components/FilterManager/filters/QueueType";
import Rank from "@ugg/shared/components/FilterManager/filters/Rank";
import Region from "@ugg/shared/components/FilterManager/filters/Region";
import RoleSplit from "@ugg/shared/components/FilterManager/filters/RoleSplit";
import { TIER_LIST_PAGES } from "@ugg/shared/pages/tier-list-pages";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";

const TierLists = (props) => {
  const { userSettings, refetchQueries, setSettingsNotification } = props;
  const { filterDefaults } = userSettings;
  const { filterTierList } = filterDefaults;

  const setMissingDefaultValues = (filters) => {
    let newFilters = { ...filters };
    delete newFilters.__typename;

    const defaultParams = validParams[TIER_LIST_PAGES.DEFAULT_TIER_LIST].default;
    for (let [key, value] of Object.entries(defaultParams)) {
      if (key === "region") {
        if (!filters["regionId"]) {
          newFilters["regionId"] = value;
        }
      } else if (key === "allChampions") {
        if (filters["showAllChampions"] === null) {
          newFilters["showAllChampions"] = value === "true";
        }
      } else if (key === "rank") {
        if (!filters["mmr"]) {
          newFilters["mmr"] = value;
        }
      } else if (key === "role" || key === "queueType") {
        if (!filters[key]) {
          newFilters[key] = value;
        }
      }
    }

    return newFilters;
  };

  const [newFilterDefaults, setNewFilterDefaults] = useState(setMissingDefaultValues(filterTierList));

  const [updateSettings, { loading, data, error }] = useMutation(UPDATE_SETTINGS, {
    variables: {
      filterTierList: newFilterDefaults,
    },
    refetchQueries,
    onCompleted: (data) => {
      setSettingsNotification(true);
      window && window.scrollTo(0, 0);
    },
    onError: (error) => {
      setSettingsNotification(false);
    },
  });

  const onFilterChange = (type, value) => {
    if (type === "region") {
      setNewFilterDefaults({ ...newFilterDefaults, regionId: value.value });
    } else if (type === "showAllChampions") {
      if (value.value) {
        setNewFilterDefaults({ ...newFilterDefaults, showAllChampions: true });
      } else {
        setNewFilterDefaults({ ...newFilterDefaults, showAllChampions: false });
      }
    } else if (type === "queueType") {
      let newValue = {
        queueType: value.value,
      };

      if (value.value === "normal_aram" || value.value === "nexus_blitz") {
        newValue["role"] = "all";
      }

      if (
        value.value === "normal_aram" ||
        value.value === "nexus_blitz" ||
        value.value === "normal_blind_5x5" ||
        value.value === "normal_draft_5x5" ||
        value.value === "one_for_all"
      ) {
        newValue["mmr"] = "overall";
      } else {
        newValue["mmr"] = "emerald_plus";
      }

      setNewFilterDefaults({ ...newFilterDefaults, ...newValue });
    } else if (type === "rank") {
      setNewFilterDefaults({ ...newFilterDefaults, mmr: value.value });
    } else if (type === "role") {
      setNewFilterDefaults({ ...newFilterDefaults, role: value.value });
    }
  };

  let filterProps = {
    filters: newFilterDefaults,
    onFilterChange,
  };

  return (
    <div className="settings-section email-subscriptions">
      <div className="section-content">
        <HeaderBullet className="mb-[24px]">Tier Lists</HeaderBullet>
        <div className="field-group">
          <div className="field-label _with-helper-text">{"Show All Champions"}</div>
          <div className="settings_helper_text">
            {"Enable to see all champions in the tier list unfiltered by pick rate, unlock the glorious Jarvan IV support."}
          </div>
          <div className="flex items-center mt-[17px]">
            <Checkbox
              checkboxId="show-all-champions"
              isChecked={newFilterDefaults.showAllChampions}
              onChange={(e) => {
                onFilterChange("showAllChampions", { value: e.target.checked });
              }}
            />
            <label className="ml-[8px] text-white leading-normal cursor-pointer" htmlFor="show-all-champions">
              Enable
            </label>
          </div>
        </div>
        <div className="field-group">
          <div className="field-label _with-helper-text">{"Default Region"}</div>
          <div className="settings_helper_text">{"Change to your preferred region for overall statistics."}</div>
          <div className="settings-filter-container">
            <Region {...filterProps} options={TIER_LIST_OPTIONS.region} disableTooltip />
          </div>
        </div>
        <div className="field-group">
          <div className="field-label _with-helper-text">{"Default Queue Type"}</div>
          <div className="settings_helper_text">
            {"Play only ARAM? Care only about the Normal Blind meta? Change to your main queue type."}
          </div>
          <div className="settings-filter-container">
            <QueueType {...filterProps} options={TIER_LIST_OPTIONS.queueType} disableTooltip />
          </div>
        </div>
        <div className="field-group">
          <div className="field-label _with-helper-text">{"Default Role"}</div>
          <div className="settings_helper_text">
            {"Mid only or feed? Only care about the Support meta? Change this setting to that role."}
          </div>
          <div className="settings-filter-container">
            <RoleSplit {...filterProps} options={ALL_ROLE_OPTIONS} disableTooltip disableByQueueType />
          </div>
        </div>
        <div className="field-group">
          <div className="field-label _with-helper-text">{"Default Rank"}</div>
          <div className="settings_helper_text">
            {
              "If you're in Iron and only care about Iron and not the hoity toity Platinum Plus players, this is the setting for you."
            }
          </div>
          <div className="settings-filter-container">
            <Rank {...filterProps} options={TIER_LIST_OPTIONS.rank} disableTooltip disableByQueueType />
          </div>
        </div>
        <ButtonSuccess
          className="save-settings-btn btn-light-blue"
          label="Save Changes"
          isLoading={loading}
          onClick={updateSettings}
        />
      </div>
    </div>
  );
};

export default TierLists;
